import countryTimeZone from "countries-and-timezones";
import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { Icon, icons } from "../../../components/Icon";
import { countryPhoneCodes } from "../../../utils/countryPhoneCodes";
import { BackButton, PrimaryButton } from "../../AuthNew/shared/authStyles";
import { motion } from "../../AuthNew/shared/motionUtils";
import {
  ButtonGroup,
  ModalTitle,
  slideInRight,
} from "../shared/onboardingStyles";

// Progress Bar (Rectangle 5) - matches other steps
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: #eef1f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: #aa8ef6;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

// Phone Input Field
const PhoneInputField = styled.input`
  width: 100%;
  height: 40px;
  border: 1px solid #999faa;
  border-radius: 8px;
  padding: 10px 12px;
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: #000e2a;
  background: white;
  outline: none;

  &::placeholder {
    color: #666e7f;
  }

  &:focus {
    border-color: #2266ef;
  }
`;

// Privacy Message
const PrivacyMessage = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  color: #353a46;
  margin: 0;
`;

// Skip Text
const SkipText = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #353a46;
  text-align: center;
  width: 100%;
  margin: 0;
  cursor: pointer;

  &:hover {
    color: #000e2a;
  }
`;

const Step4GetHelp = ({
  data,
  onChange,
  onNext,
  onBack,
  progress = 83.33, // Default progress for step 5 of 6
}) => {
  const [phone, setPhone] = useState(data.phoneNumber || "");
  const phoneRef = useRef(null);

  // Country code detection based on timezone
  const fetchCountryCodeFromTimeZone = useCallback(() => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const country = countryTimeZone.getTimezone(timeZone)?.countries?.[0];
    const countryCode = country?.toLowerCase() || "us";

    // If no existing phone number, pre-populate with country code
    if (!data.phoneNumber) {
      const phoneCode = countryPhoneCodes[countryCode];
      if (phoneCode) {
        setPhone(phoneCode);
      }
    }
  }, [data.phoneNumber]);

  useEffect(() => {
    fetchCountryCodeFromTimeZone();
  }, [fetchCountryCodeFromTimeZone]);

  const formatPhoneNumber = useCallback(value => {
    // Check if it starts with + and preserve it
    const hasPlus = value.startsWith("+");

    // Remove all non-digit characters except + at the beginning
    let phoneNumber = value.replace(/[^\d+]/g, "");
    if (hasPlus && !phoneNumber.startsWith("+")) {
      phoneNumber = "+" + phoneNumber;
    }

    // Don't format if empty
    if (phoneNumber.length === 0) return "";

    // Extract just the digits for formatting
    const digits = phoneNumber.replace(/\D/g, "");

    // Don't format if too short
    if (digits.length <= 3) return phoneNumber;

    // Format based on length
    if (digits.length <= 6) {
      return `${hasPlus ? "+" : ""}(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else if (digits.length <= 10) {
      return `${hasPlus ? "+" : ""}(${digits.slice(0, 3)}) ${digits.slice(
        3,
        6,
      )}-${digits.slice(6)}`;
    } else {
      // International format
      const countryCode = digits.slice(0, digits.length - 10);
      const localNumber = digits.slice(-10);
      return `+${countryCode} (${localNumber.slice(0, 3)}) ${localNumber.slice(
        3,
        6,
      )}-${localNumber.slice(6)}`;
    }
  }, []);

  const handlePhoneChange = useCallback(
    e => {
      const value = e.target.value;
      const formatted = formatPhoneNumber(value);
      setPhone(formatted);
    },
    [formatPhoneNumber],
  );

  const handlePhoneKeyDown = useCallback(
    e => {
      // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
      if (
        [8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) ||
        (e.keyCode === 67 && e.ctrlKey === true) ||
        (e.keyCode === 86 && e.ctrlKey === true) ||
        (e.keyCode === 88 && e.ctrlKey === true)
      ) {
        return;
      }
      // Allow + at the beginning
      if (e.key === "+" && phone.length === 0) {
        return;
      }
      // Allow digits, spaces, dashes, parentheses
      if (e.key.match(/[\d\s\-()]/)) {
        return;
      }
      // Block everything else
      e.preventDefault();
    },
    [phone.length],
  );

  const isValidPhoneNumber = useCallback(phoneNumber => {
    if (!phoneNumber || phoneNumber.trim() === "") return false;

    // Match: optional +, then 10-15 digits (spaces/dashes allowed)
    const phoneRegex = /^\+?[\d\s\-()]{10,20}$/;

    if (!phoneRegex.test(phoneNumber.trim())) return false;

    // Count actual digits
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  }, []);

  const sanitizePhoneNumber = useCallback(phoneNumber => {
    // Remove all formatting characters, keep only digits and +
    return phoneNumber.replace(/[^\d+]/g, "");
  }, []);

  const handleNext = useCallback(() => {
    // Only save if it's a valid phone number, otherwise save null
    const phoneNumber = phone.trim();
    const validPhone = isValidPhoneNumber(phoneNumber)
      ? sanitizePhoneNumber(phoneNumber)
      : null;
    onChange({ phoneNumber: validPhone });
    onNext();
  }, [phone, isValidPhoneNumber, sanitizePhoneNumber, onChange, onNext]);

  const handleSkip = useCallback(() => {
    onChange({ phoneNumber: null });
    onNext();
  }, [onChange, onNext]);

  // Handle Enter key press
  const handleKeyDown = useCallback(
    e => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleNext();
      }
    },
    [handleNext],
  );

  // Add keyboard event listener
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  // Auto-focus phone input on component mount
  useEffect(() => {
    if (phoneRef.current) {
      phoneRef.current.focus();
    }
  }, []);

  return (
    <motion.div
      variants={slideInRight}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Progress Bar */}
      <ProgressBarContainer>
        <ProgressBarFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
      </ProgressBarContainer>

      {/* Title */}
      <div style={{ marginTop: "32px" }}>
        <ModalTitle>Last thing!</ModalTitle>
      </div>

      {/* Form Section */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          marginTop: "32px",
          width: "100%",
          maxWidth: "400px",
        }}
      >
        {/* Phone Input */}
        <PhoneInputField
          ref={phoneRef}
          type="tel"
          value={phone}
          onChange={handlePhoneChange}
          onKeyDown={handlePhoneKeyDown}
          placeholder="Your phone number (optional)"
        />

        {/* Privacy Message */}
        <PrivacyMessage>🔒 We'll never share your number</PrivacyMessage>

        {/* Back and Start Work Buttons */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          <ButtonGroup style={{ maxWidth: "400px" }}>
            <BackButton
              type="button"
              onClick={onBack}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              height="40px"
              style={{
                background: "#e9f0fe",
                borderRadius: "32px",
                padding: "8px 16px",
                width: "100px",
                fontSize: "14px",
                fontWeight: 700,
                color: "#000e2a",
                border: "none",
              }}
            >
              <Icon component={icons.back} />
              Back
            </BackButton>
            <PrimaryButton
              type="button"
              onClick={handleNext}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              height="40px"
              fontSize="16px"
              style={{
                flex: 1,
                borderRadius: "32px",
                padding: "8px 16px",
                background:
                  "linear-gradient(to right, #2266ef 0%, #6c2ef2 100%)",
                color: "white",
                fontWeight: 700,
              }}
            >
              Start Work
            </PrimaryButton>
          </ButtonGroup>

          {/* Skip Text */}
          <SkipText onClick={handleSkip}>Skip & Start Working</SkipText>
        </div>
      </div>
    </motion.div>
  );
};

export default Step4GetHelp;
